<template>
  <transition>
    <div
      v-if="filterToggle"
      class="pt-2"
    >
      <b-form class="border-dashed">
        <b-row>
          <b-col md="4">
            <b-form-group
              :label="$t(filterClass.getLabel(ApprovedScientificTopics.DC_TYPE))"
              :label-for="ApprovedScientificTopics.DC_TYPE"
            >
              <v-select
                :id="ApprovedScientificTopics.DC_TYPE"
                v-model="filters[ApprovedScientificTopics.DC_TYPE]"
                :reduce="(option) => option.id"
                :options="dcTypes"
                :get-option-label="(option) => langName(option.name)"
                :placeholder="$t('All')"
              />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group
              :label="$t(filterClass.getLabel(ApprovedScientificTopics.ORGANIZATION))"
              :label-for="ApprovedScientificTopics.ORGANIZATION"
            >
              <v-select
                :id="ApprovedScientificTopics.ORGANIZATION"
                v-model="filters[ApprovedScientificTopics.ORGANIZATION]"
                :reduce="(option) => option.id"
                :options="organizationOptions"
                :filterable="false"
                :get-option-label="organizationLabel"
                :placeholder="$t('All')"
                @input="setSelectedOrganization"
                @search="fetchOrganizations"
              />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group
              :label="$t(filterClass.getLabel(ApprovedScientificTopics.DIRECTION))"
              :label-for="ApprovedScientificTopics.DIRECTION"
            >
              <v-select
                :id="ApprovedScientificTopics.DIRECTION"
                v-model="filters[ApprovedScientificTopics.DIRECTION]"
                :reduce="(option) => option.id"
                :options="directionOptions"
                :filterable="false"
                :get-option-label="directionLabel"
                :placeholder="$t('All')"
                @input="setSelectedDirection"
                @search="fetchDirections"
              />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group
              :label="$t(filterClass.getLabel(ApprovedScientificTopics.PERIOD))"
              :label-for="ApprovedScientificTopics.PERIOD"
            >
              <v-select
                :id="ApprovedScientificTopics.PERIOD"
                v-model="filters[ApprovedScientificTopics.PERIOD]"
                :reduce="(option) => option.id"
                :options="periods"
                :get-option-label="periodLabel"
                :placeholder="$t('All')"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-button
              class="mr-1"
              variant="success"
              @click="onFilter"
            >
              <feather-icon icon="LFilterIcon" />
              {{ $t('Filter') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </div>
  </transition>
</template>

<script>
import vSelect from 'vue-select'
import {
  BButton, BCol, BForm, BFormGroup, BRow,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import axios from '@/libs/axios'
import ApprovedScientificTopics from '@/views/components/filters/service/ApprovedScientificTopics'

export default {
  name: 'ApprovedScientificTopicsFilterComponent',
  components: {
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BRow,
    vSelect,
  },
  directives: {
    Ripple,
  },
  props: {
    filterToggle: {
      type: Boolean,
      required: true,
    },
    filters: {
      type: [Array, Object],
      default: () => ({}),
    },
    filterClass: {
      type: [Object, null],
      default: null,
    },
    selectedFilters: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    return {
      directions: [],
      organizations: [],
      periods: [],
      selectedDirection: null,
      selectedOrganization: null,
    }
  },
  computed: {
    ApprovedScientificTopics() {
      return ApprovedScientificTopics
    },
    dcTypes() {
      return this.$store.state.staticModule.dcTypes
    },
    directionOptions() {
      return this.withSelectedOption(this.directions, this.selectedDirection)
    },
    organizationOptions() {
      return this.withSelectedOption(this.organizations, this.selectedOrganization)
    },
  },
  created() {
    this.normalizeFilters()
    this.fetchFilters()
  },
  methods: {
    periodLabel(option) {
      if (!option || typeof option !== 'object') return ''
      return option.admission_year
    },
    directionLabel(option) {
      if (!option || typeof option !== 'object') return ''
      const code = option.code ? `${option.code} - ` : ''
      return `${code}${this.optionName(option)}`
    },
    organizationLabel(option) {
      if (!option || typeof option !== 'object') return ''
      return this.optionName(option)
    },
    optionName(option) {
      return this.langName(option.name || option.title || option.text || option.short_name) || `${option.id || ''}`
    },
    langName(value) {
      if (!value) return ''
      if (typeof value === 'string') return value
      return this.$langName(value)
    },
    getList(data) {
      const list = data.results || data.data || data.items || data
      return Array.isArray(list) ? list : []
    },
    withSelectedOption(options, selected) {
      if (!selected || options.find(option => this.sameId(option.id, selected.id))) return options
      return [selected, ...options]
    },
    sameId(first, second) {
      return String(first) === String(second)
    },
    fetchFilters() {
      const periodYears = [2023, 2024, 2025, 2026]
      Promise.all([
        axios.get('/directions/', { params: { limit: 20 } }),
        axios.get('/organizations', { params: { limit: 20 } }),
        this.$store.dispatch('staticModule/dcType'),
        axios.get('/periods/', { params: { limit: 9999, ordering: '-admission_year' } }),
      ]).then(([directions, organizations, , periods]) => {
        this.directions = this.getList(directions.data)
        this.organizations = this.getList(organizations.data)
        this.periods = this.getList(periods.data)
          .filter(period => periodYears.includes(period.admission_year))
          .sort((first, second) => periodYears.indexOf(first.admission_year) - periodYears.indexOf(second.admission_year))
        this.initSelectedOptions()
      })
    },
    initSelectedOptions() {
      const directionId = this.filters[ApprovedScientificTopics.DIRECTION]
      const organizationId = this.filters[ApprovedScientificTopics.ORGANIZATION]

      if (directionId) {
        this.setSelectedDirection(directionId)
      }

      if (organizationId) {
        this.setSelectedOrganization(organizationId)
      }
    },
    fetchDirections(search, loading) {
      loading(true)
      axios.get('/directions/', { params: { search, limit: 20 } })
        .then(({ data }) => {
          this.directions = this.getList(data)
        })
        .finally(() => loading(false))
    },
    fetchOrganizations(search, loading) {
      loading(true)
      axios.get('/organizations', { params: { search, limit: 20 } })
        .then(({ data }) => {
          this.organizations = this.getList(data)
        })
        .finally(() => loading(false))
    },
    normalizeFilters() {
      [
        ApprovedScientificTopics.DIRECTION,
        ApprovedScientificTopics.ORGANIZATION,
        ApprovedScientificTopics.PERIOD,
      ].forEach(field => {
        if (typeof this.filters[field] === 'string') {
          this.filters[field] = Number(this.filters[field].split(',').filter(Boolean)[0])
        } else if (Array.isArray(this.filters[field])) {
          this.filters[field] = this.filters[field][0] || null
        } else if (this.filters[field]) {
          this.filters[field] = Number(this.filters[field])
        }
      })

      if (Array.isArray(this.filters[ApprovedScientificTopics.DC_TYPE])) {
        this.filters[ApprovedScientificTopics.DC_TYPE] = this.filters[ApprovedScientificTopics.DC_TYPE][0] || null
      } else if (this.filters[ApprovedScientificTopics.DC_TYPE]) {
        this.filters[ApprovedScientificTopics.DC_TYPE] = Number(this.filters[ApprovedScientificTopics.DC_TYPE])
      }
    },
    onFilter() {
      this.$emit('onFilter', this.filterClass.convertToQuery(this.filters))
    },
    setSelectedDirection(id) {
      this.selectedDirection = this.directions.find(direction => this.sameId(direction.id, id)) || null
    },
    setSelectedOrganization(id) {
      this.selectedOrganization = this.organizations.find(organization => this.sameId(organization.id, id)) || null
    },
  },
}
</script>
